import { PortableText } from "@portabletext/react";
import type { PortableTextBlock } from "../../lib/sanity";
import { buildPortableTextImageProps } from "../../lib/sanity-image-urls";

interface PortableTextContentProps {
  value: PortableTextBlock[] | null | undefined;
}

export function PortableTextContent({ value }: PortableTextContentProps) {
  const blocks = Array.isArray(value) ? value : [];
  if (blocks.length === 0) return null;

  return (
    <PortableText
      value={blocks}
      components={{
        block: {
          normal: ({ children }) => (
            <p className="text-gray-700 mb-4 leading-relaxed">{children}</p>
          ),
          h5: ({ children }) => (
            <h5 className="text-lg font-semibold text-gray-900 mt-6 mb-2">
              {children}
            </h5>
          ),
        },
        marks: {
          link: ({ children, value: markValue }) => {
            const href = markValue?.url ?? markValue?.href ?? "#";
            return (
              <a
                href={href}
                className="text-primary-600 hover:text-primary-700 underline"
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
              >
                {children}
              </a>
            );
          },
        },
        types: {
          image: ({ value: imageValue }) => {
            if (!imageValue?.asset) return null;
            const img = buildPortableTextImageProps(imageValue, 800);
            return (
              <figure className="my-6">
                <img
                  src={img.src}
                  srcSet={img.srcSet}
                  sizes={img.sizes}
                  width={img.width}
                  height={img.height}
                  alt={(imageValue.alt as string) ?? ""}
                  className="rounded-lg w-full max-w-2xl mx-auto max-h-[500px] object-contain"
                  decoding="async"
                />
              </figure>
            );
          },
        },
      }}
    />
  );
}
